/**
 * «Бухгалтер» — разговор с бухгалтером-калькулятором (D236, D238).
 *
 * Это переписка, а не поле вопроса: бухгалтер помнит разговор, и «а по
 * Метро?» после вопроса о поставщике — продолжение, а не новый вопрос.
 * Под каждым ответом — «что я сделал»: цифра, собранная в пять приёмов,
 * должна быть разбираема в пять приёмов.
 *
 * Считает не портал и не модель: каждое число в ответе проверено кодом на
 * то, что оно взято из результатов инструментов (D239).
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import type { Navigation } from '../../api';
import { Notice } from '../../ui';
import { accountantApi } from './api';
import type { Answer, Turn } from './api';
import './accountant.css';

const EXAMPLES = [
  'Что с накладными за неделю и что ждёт проведения?',
  'Сколько мы должны поставщикам и что просрочено?',
  'Какой фудкост был за прошлую неделю?',
  'Что ты проверил и что осталось не исправлено?',
];

const KEY = ['accountant', 'dialog'];

export default function Accountant(_: { nav: Navigation }) {
  const client = useQueryClient();
  const [question, setQuestion] = useState('');
  const [lastDid, setLastDid] = useState<Answer | null>(null);
  const bottom = useRef<HTMLDivElement | null>(null);

  const dialog = useQuery<{ turns: Turn[] }, ApiError>({
    queryKey: KEY,
    queryFn: accountantApi.dialog,
  });

  const ask = useMutation<Answer, ApiError, string>({
    mutationFn: (text) => accountantApi.ask(text),
    onSuccess: (answer) => {
      setLastDid(answer);
      void client.invalidateQueries({ queryKey: KEY });
    },
  });

  const fresh = useMutation<{ turns: Turn[] }, ApiError, void>({
    mutationFn: accountantApi.fresh,
    onSuccess: (data) => {
      setLastDid(null);
      client.setQueryData(KEY, data);
    },
  });

  const turns = dialog.data?.turns ?? [];
  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: 'end' });
  }, [turns.length, ask.isPending]);

  const send = (text: string) => {
    const cleaned = text.trim();
    if (!cleaned || ask.isPending) return;
    setQuestion('');
    ask.mutate(cleaned);
  };

  if (dialog.error) {
    return (
      <Notice kind="error">
        {dialog.error.detail ?? 'Разговор с бухгалтером сейчас не открывается.'}
      </Notice>
    );
  }

  return (
    <section className="acct" aria-label="Бухгалтер-калькулятор">
      <header className="acct__head">
        <div>
          <h1 className="acct__title">Бухгалтер-калькулятор</h1>
          <p className="acct__lead">
            Приходы, приёмка накладных, долги поставщикам, закупочные цены, себестоимость.
            Каждую цифру беру из данных ресторана; чего нет — скажу прямо. Документы в
            StoreHouse из разговора не меняю.
          </p>
        </div>
        <button
          type="button"
          className="button"
          disabled={fresh.isPending || turns.length === 0}
          onClick={() => fresh.mutate()}
        >
          Новый разговор
        </button>
      </header>

      <div className="acct__log" role="log" aria-live="polite">
        {turns.length === 0 && !ask.isPending && (
          <div className="acct__empty">
            <p>С чего начать:</p>
            <ul>
              {EXAMPLES.map((example) => (
                <li key={example}>
                  <button type="button" className="acct__example" onClick={() => send(example)}>
                    {example}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {turns.map((turn, index) => (
          <div key={`${index}-${turn.at}`} className={`acct__msg acct__msg--${turn.role}`}>
            <p className="acct__text">{turn.text}</p>
            {turn.role === 'assistant' && turn.verdict === 'fallback' && (
              <p className="acct__meta">Показано посчитанное как есть — без пересказа.</p>
            )}
          </div>
        ))}
        {ask.isPending && (
          <div className="acct__msg acct__msg--assistant acct__msg--pending">
            <p className="acct__text">Смотрю данные…</p>
          </div>
        )}
        {lastDid?.did && !ask.isPending && (
          <p className="acct__meta">Что я сделал: {lastDid.did}.</p>
        )}
        {lastDid?.note && !ask.isPending && <p className="acct__meta">({lastDid.note})</p>}
        {ask.error && (
          <Notice kind="error">{ask.error.detail ?? 'Не получилось спросить. Ещё раз?'}</Notice>
        )}
        <div ref={bottom} />
      </div>

      <form
        className="acct__form"
        onSubmit={(event) => {
          event.preventDefault();
          send(question);
        }}
      >
        <label className="acct__field">
          <span className="sr-only">Вопрос бухгалтеру</span>
          <textarea
            className="input acct__input"
            rows={2}
            value={question}
            maxLength={2000}
            placeholder="Спросите, как спросили бы живого бухгалтера"
            aria-label="Вопрос бухгалтеру"
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(question);
              }
            }}
          />
        </label>
        <button
          type="submit"
          className="button button--primary"
          disabled={!question.trim() || ask.isPending}
        >
          {ask.isPending ? 'Считаю…' : 'Спросить'}
        </button>
      </form>
    </section>
  );
}
